/**
 * Track types — shared data model for imported flight tracks. All parsers
 * (GPX, KML, CSV/TSV, GeoJSON) and the DuckDB-wasm backed importer produce
 * values matching these shapes so downstream code (map rendering, stats,
 * persistence) doesn't need to know which format the track came from.
 */

export type TrackFormat = 'gpx' | 'kml' | 'csv' | 'tsv' | 'geojson';

export interface TrackPoint {
  /** Epoch milliseconds. Optional \u2014 CSVs without time columns and
   *  untimed GPX tracks will omit this. */
  timestamp?: number;
  latitude: number;
  longitude: number;
  /** Altitude in feet MSL. */
  altitudeFt?: number;
  /** Ground speed in knots. */
  groundSpeedKts?: number;
  /** True heading in degrees (0\u2013360). */
  heading?: number;
}

export interface TrackStats {
  distanceNm: number;
  durationMs: number;
  maxAltFt: number;
  minAltFt: number;
  maxGsKts: number;
  avgGsKts: number;
  startTime: number | null;
  endTime: number | null;
  climbFt: number;
  descentFt: number;
}

export interface Track {
  id: string;
  name: string;
  format: TrackFormat;
  importedAt: number;
  points: TrackPoint[];
  stats: TrackStats;
  /** Original filename for display. */
  sourceFilename?: string;
  /** Notes / free-text description. */
  notes?: string;
}

/** Return the TrackFormat implied by a filename, or null if unknown. */
export function detectFormatFromFilename(filename: string): TrackFormat | null {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.gpx')) return 'gpx';
  if (lower.endsWith('.kml')) return 'kml';
  if (lower.endsWith('.csv')) return 'csv';
  if (lower.endsWith('.tsv')) return 'tsv';
  if (lower.endsWith('.geojson') || lower.endsWith('.json')) return 'geojson';
  return null;
}

/** Sniff format from file contents as a fallback when the extension is wrong. */
export function detectFormatFromContent(content: string): TrackFormat | null {
  const head = content.slice(0, 2048).trimStart();
  if (head.startsWith('<?xml') || head.startsWith('<')) {
    if (/<gpx\b/i.test(head)) return 'gpx';
    if (/<kml\b/i.test(head)) return 'kml';
  }
  if (head.startsWith('{') || head.startsWith('[')) return 'geojson';
  if (head.includes('\t') && !head.includes(',\t')) return 'tsv';
  if (head.includes(',')) return 'csv';
  return null;
}
